import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { doc, getDoc } from 'firebase/firestore'
import { projectFirestore } from '@/firebase/config'

export function useMarketingPermission() {
  const store = useStore()
  
  const popupOpen = ref(false)
  const hasCheckedPermission = ref(false)
  const userHasResponded = ref(false)

  const currentUser = computed(() => store.state.currentUser)

  // Configuration for popup timing - show after 3 weeks (21 days)
  const PERMISSION_DELAY_DAYS = 21

  // Get user's account age in days
  const getUserAccountAge = () => {
    if (!currentUser.value?.metadata?.creationTime) {
      return 0
    }

    const creationTime = new Date(currentUser.value.metadata.creationTime)
    const now = new Date()
    const daysSinceCreation = (now - creationTime) / (1000 * 60 * 60 * 24)
    
    return Math.floor(daysSinceCreation)
  }

  // Check if user has been using the system long enough
  const hasUserBeenActiveLongEnough = () => {
    const accountAge = getUserAccountAge()
    const isActiveLongEnough = accountAge >= PERMISSION_DELAY_DAYS
    
    return isActiveLongEnough
  }

  // Check if user has already responded
  const checkPermissionResponse = async () => {
    if (!currentUser.value) {
      return
    }

    try {
      const userRef = doc(projectFirestore, 'users', currentUser.value.uid)
      const userDoc = await getDoc(userRef)
      
      if (userDoc.exists()) {
        const userData = userDoc.data()
        // Check if user has responded to marketing permission request
        const hasResponded = userData.marketingPermission?.respondedAt !== undefined
        
        userHasResponded.value = hasResponded
        
        // If user has responded, close popup if it's open
        if (hasResponded && popupOpen.value) {
          popupOpen.value = false
        }
      }
    } catch (error) {
      console.error('Error checking marketing permission response:', error)
    } finally {
      hasCheckedPermission.value = true
    }
  }

  // Show popup if user hasn't responded and has been active long enough
  const showPopupIfNeeded = async () => {
    if (!currentUser.value) return

    // Always check response status first
    await checkPermissionResponse()
    
    // Check if user has been active long enough
    const userActiveLongEnough = hasUserBeenActiveLongEnough()
    
    // Check subscription status - only show to users with active subscriptions
    const hasSubscription = store.state.hasSubscribed === true
    
    // Check configuration completion - only show to users who have completed setup
    const configComplete = store.state.userConfig?.configComplete === true
    
    // Check if we're on a dashboard page (exclude login page)
    const isOnDashboard = store.state.currentDashboardView?.dashboard || 
                         (window.location.pathname.includes('/dashboard') && 
                          window.location.pathname !== '/') ||
                         window.location.pathname.includes('/backoffice')
    
    // Show popup if user hasn't responded, has been active long enough, has subscription, 
    // has completed config, and we're on the dashboard
    if (!userHasResponded.value && 
        userActiveLongEnough && 
        hasSubscription && 
        configComplete && 
        isOnDashboard) {
      // Wait a bit for the page to load, then show popup
      setTimeout(() => {
        popupOpen.value = true
      }, 3000) // Show after 3 seconds
    } 
  }

  // Close popup
  const closePopup = () => {
    popupOpen.value = false
  }

  // Manual test function for debugging
  const testPermissionSystem = async () => {
    // Force a fresh check
    hasCheckedPermission.value = false
    await showPopupIfNeeded()
  }

  // Force show popup (for testing)
  const forceShowPopup = () => {
    popupOpen.value = true
  }

  // Check permission on mount and when user changes
  onMounted(() => {
    if (currentUser.value) showPopupIfNeeded()
  })

  // Watch for user changes
  watch(currentUser, async (newUser) => {
    if (newUser) {
      await showPopupIfNeeded()
    }
  })

  // Expose test functions globally for debugging
  if (typeof window !== 'undefined') {
    window.testMarketingPermissionSystem = testPermissionSystem
    window.forceShowMarketingPermission = forceShowPopup
    window.getMarketingPermissionAccountAge = getUserAccountAge
  }

  return {
    popupOpen,
    userHasResponded,
    hasCheckedPermission,
    showPopupIfNeeded,
    closePopup,
    checkPermissionResponse,
    refreshCompletionStatus: checkPermissionResponse,
    hasUserBeenActiveLongEnough,
    getUserAccountAge,
    testPermissionSystem,
    forceShowPopup
  }
}
